// 2026-05-25 推薦名單 — 全面改版（截圖 design）
// 2026-06-08 細部對齊 Sean 規格：統計卡/donut/層級tab/搜尋/推薦總覽表 接 token + 圖示
import { useEffect, useMemo, useState, useContext } from "react";
import { Box, TextField, Menu, MenuItem, InputAdornment } from "@mui/material";
import dayjs from "dayjs";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

import MDTypography from "components/MDTypography";
import { getReferralUsers } from "../../../../api/userApi";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS, TYPO, FONT } from "theme/tokens";

// 橘色（同 #F36D26）以透明度做深→淺：深1 / 中0.5 / 淺0.3
const ORANGE_OPACITY = [1, 0.5, 0.3];

// Email 脫敏：保留前五字
function maskEmail(email) {
  if (!email) return "-";
  const prefix = email.slice(0, 5);
  return `${prefix}*****`;
}

// 名稱脫敏：保留前四字 + 固定 ****（固定數量，避免用 * 數推測全名長度）
function maskName(name) {
  if (!name || name === "-") return "-";
  return `${name.slice(0, 4)}****`;
}

export default function ReferralList() {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("ReferralTabs", "referral/ReferralTabs/ReferralTabs_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.ReferralTabs || { Main: {} },
    [componentLanguageData]
  );

  const [rows, setRows] = useState([]);
  const [levelFilter, setLevelFilter] = useState(0); // 0=全部, 1/2/3
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [anchorElPageSize, setAnchorElPageSize] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getReferralUsers();
        const normalized = (data || []).map((u, idx) => ({
          id: u.id ?? idx,
          name: u.name || "-",
          email: u.email || "",
          level: Number(u.level) || 1,
          createdAt: u.createdAt ? dayjs(u.createdAt).format("YYYY-MM-DD") : "-",
          parentName: u.parentName || "-",
          parentEmail: u.parentEmail || "-",
          registerType: u.registerType || "EMAIL",
          subscriptionStatus: u.subscriptionStatus || "NOT_SUBSCRIBED",
        }));
        setRows(normalized);
      } catch (err) {
        console.error("取得推薦名單失敗:", err);
      }
    })();
  }, []);

  // 統計
  const totalCount = rows.length;
  const level1Count = rows.filter((r) => r.level === 1).length;
  const level2Count = rows.filter((r) => r.level === 2).length;
  const level3Count = rows.filter((r) => r.level === 3).length;

  // donut data
  const levelPieData = [
    { name: LANGUAGE.Main.Level1 || "第一層", value: level1Count, opacity: ORANGE_OPACITY[0] },
    { name: LANGUAGE.Main.Level2 || "第二層", value: level2Count, opacity: ORANGE_OPACITY[1] },
    { name: LANGUAGE.Main.Level3 || "第三層", value: level3Count, opacity: ORANGE_OPACITY[2] },
  ];

  const registerStats = useMemo(() => {
    const counts = rows.reduce((acc, r) => {
      acc[r.registerType] = (acc[r.registerType] || 0) + 1;
      return acc;
    }, {});
    return [
      { name: LANGUAGE.Main.RegisterEmail || "Email", value: counts.EMAIL || 0, opacity: ORANGE_OPACITY[0] },
      { name: LANGUAGE.Main.RegisterGoogle || "Google", value: counts.GOOGLE || 0, opacity: ORANGE_OPACITY[1] },
    ];
  }, [rows, LANGUAGE.Main.RegisterEmail, LANGUAGE.Main.RegisterGoogle]);

  const subscriptionStats = useMemo(() => {
    const counts = rows.reduce((acc, r) => {
      acc[r.subscriptionStatus] = (acc[r.subscriptionStatus] || 0) + 1;
      return acc;
    }, {});
    return [
      { name: LANGUAGE.Main.SubSubscribed || "有訂閱", value: counts.SUBSCRIBED || 0, opacity: ORANGE_OPACITY[0] },
      { name: LANGUAGE.Main.SubNotSubscribed || "未訂閱", value: counts.NOT_SUBSCRIBED || 0, opacity: ORANGE_OPACITY[1] },
    ];
  }, [rows, LANGUAGE.Main.SubSubscribed, LANGUAGE.Main.SubNotSubscribed]);

  // table 過濾
  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      if (levelFilter !== 0 && row.level !== levelFilter) return false;
      if (!keyword) return true;
      const k = keyword.toLowerCase();
      return (
        row.name.toLowerCase().includes(k) ||
        row.email.toLowerCase().includes(k) ||
        row.parentName.toLowerCase().includes(k) ||
        row.parentEmail.toLowerCase().includes(k)
      );
    });
  }, [rows, levelFilter, keyword]);

  // 分頁
  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pagedRows = filteredRows.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    setPage(1);
  }, [levelFilter, keyword, pageSize]);

  const footerText = (LANGUAGE.Main.Footer || "顯示 {{from}}-{{to}} 筆，共 {{total}} 筆資料")
    .replace("{{from}}", total === 0 ? 0 : (page - 1) * pageSize + 1)
    .replace("{{to}}", Math.min(page * pageSize, total))
    .replace("{{total}}", total);

  // 大數字（Outfit 32 / 500 / 橘）
  const bigNum = { fontFamily: FONT.OUTFIT, fontSize: "32px", fontWeight: 500, color: COLORS.main, lineHeight: 1.1 };
  // 橘色小槓（4×21 / radius 20）
  const orangeBar = { width: 4, height: 21, background: COLORS.main, borderRadius: "20px", flexShrink: 0 };
  // 卡片外框（radius20 + 1px #E6E6E6 + 白底）
  const boxCard = { borderRadius: "20px", border: `1px solid ${COLORS.grayLighter}`, background: COLORS.white };

  // ─── 子元件：統計卡 ───
  const StatCard = ({ title, value }) => (
    <Box sx={{ ...boxCard, px: 3, py: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box display="flex" alignItems="center" gap={1}>
        <Box sx={orangeBar} />
        <MDTypography sx={{ ...TYPO.title, fontWeight: 700, color: COLORS.black }}>{title}</MDTypography>
      </Box>
      <MDTypography sx={bigNum}>{value}</MDTypography>
    </Box>
  );

  // ─── 子元件：donut chart card ───
  const DonutCard = ({ title, data, centerLabel, centerValue }) => (
    <Box sx={{ ...boxCard, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, pb: "21px", overflow: "hidden" }}>
      {/* header（橘槓 + 標題，padding24 + 下框線）*/}
      <Box sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1, p: 3, borderBottom: `1px solid ${COLORS.grayLighter}` }}>
        <Box sx={orangeBar} />
        <MDTypography sx={{ fontFamily: FONT.NOTO, fontSize: "16px", fontWeight: 700, color: COLORS.black }}>
          {title}
        </MDTypography>
      </Box>
      {/* donut */}
      <Box sx={{ height: 200, width: "100%", position: "relative" }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={80} startAngle={90} endAngle={-270} stroke="none">
              {data.map((entry, i) => (
                <Cell key={i} fill={COLORS.main} fillOpacity={entry.opacity} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center", pointerEvents: "none" }}>
          <MDTypography sx={{ ...TYPO.title, fontWeight: 500, color: COLORS.black }}>{centerLabel}</MDTypography>
          <MDTypography sx={{ ...TYPO.displayL, fontWeight: 500, color: COLORS.black }}>{centerValue}</MDTypography>
        </Box>
      </Box>
      {/* legend */}
      <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1.5}>
        {data.map((d, i) => (
          <Box key={i} display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: COLORS.main, opacity: d.opacity }} />
            <MDTypography sx={{ fontSize: "11px", color: COLORS.grayDark }}>{d.name}</MDTypography>
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: "27px" }}>
      {/* 4 統計卡（每張 260 / 卡間 24px）*/}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }, gap: 3 }}>
        <StatCard title={LANGUAGE.Main.StatTotal || "總推薦人數"} value={totalCount} />
        <StatCard title={LANGUAGE.Main.StatLevel1 || "第一層人數"} value={level1Count} />
        <StatCard title={LANGUAGE.Main.StatLevel2 || "第二層人數"} value={level2Count} />
        <StatCard title={LANGUAGE.Main.StatLevel3 || "第三層人數"} value={level3Count} />
      </Box>

      {/* 3 donut chart */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 3 }}>
        <DonutCard title={LANGUAGE.Main.ChartLevelTitle || "每層級佔比"} data={levelPieData} centerLabel={LANGUAGE.Main.ChartCenterTotal || "推薦人總數"} centerValue={totalCount} />
        <DonutCard title={LANGUAGE.Main.ChartRegisterTitle || "註冊來源統計"} data={registerStats} centerLabel={LANGUAGE.Main.ChartCenterTotal || "推薦人總數"} centerValue={totalCount} />
        <DonutCard title={LANGUAGE.Main.ChartSubscriptionTitle || "訂閱狀態統計"} data={subscriptionStats} centerLabel={LANGUAGE.Main.ChartCenterTotal || "推薦人總數"} centerValue={totalCount} />
      </Box>

      {/* 層級過濾 underline tabs（全部/第一層/第二層/第三層）*/}
      <Box sx={{ display: "flex", gap: "8px", borderBottom: `1px solid ${COLORS.grayLighter}`, width: "100%" }}>
        {[
          { key: 0, label: LANGUAGE.Main.LevelAll || "全部" },
          { key: 1, label: LANGUAGE.Main.Level1 || "第一層" },
          { key: 2, label: LANGUAGE.Main.Level2 || "第二層" },
          { key: 3, label: LANGUAGE.Main.Level3 || "第三層" },
        ].map((t) => {
          const active = levelFilter === t.key;
          return (
            <Box
              key={t.key}
              onClick={() => setLevelFilter(t.key)}
              sx={{
                flex: "1 0 0",
                height: 48,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                cursor: "pointer",
                ...TYPO.title,
                fontWeight: 700,
                color: active ? COLORS.main : COLORS.black,
                borderBottom: active ? `2px solid ${COLORS.main}` : "2px solid transparent",
                mb: "-1px",
                transition: "all 0.2s",
              }}
            >
              {t.label}
            </Box>
          );
        })}
      </Box>

      {/* 搜尋框（高 52 / radius12 / 右側灰底 Search.png）*/}
      <Box sx={{ display: "flex", height: 52, width: "100%" }}>
        <TextField
          fullWidth
          placeholder={LANGUAGE.Main.SearchPlaceholder || "搜尋關鍵字..."}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ height: 52, maxHeight: 52, m: 0 }}>
                <Box sx={{ width: 48, height: 52, background: COLORS.grayLightest, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <Box component="img" src="/images/icon/Search.png" alt="search" sx={{ width: 24, height: 24, objectFit: "contain" }} />
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 52,
              borderRadius: "12px",
              background: COLORS.white,
              pr: 0,
              overflow: "hidden",
              "& fieldset": { borderColor: COLORS.grayLighter },
              "&:hover fieldset": { borderColor: COLORS.grayMedian },
              "&.Mui-focused fieldset": { borderColor: COLORS.main, borderWidth: "1px" },
            },
            "& .MuiOutlinedInput-input": {
              ...TYPO.bodyM,
              fontWeight: 400,
              pl: "16px",
              "&::placeholder": { color: COLORS.grayMedian, opacity: 1 },
            },
          }}
        />
      </Box>

      {/* 推薦總覽 table（padding24 / radius12 / 1px#E6E6E6 / shadow-M）*/}
      <Box sx={{ width: "100%", borderRadius: "12px", border: `1px solid ${COLORS.grayLighter}`, background: COLORS.white, boxShadow: "2px 2px 8px 0 rgba(60, 59, 59, 0.15)", overflow: "hidden" }}>
        {/* header bar：推薦總覽 + 顯示 + pageSize 下拉 */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 3 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={orangeBar} />
            <MDTypography sx={{ ...TYPO.title, fontWeight: 700, color: COLORS.black }}>
              {LANGUAGE.Main.TableTitle || "推薦總覽"}
            </MDTypography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>
              {LANGUAGE.Main.Show || "顯示"}
            </MDTypography>
            <Box
              onClick={(e) => setAnchorElPageSize(e.currentTarget)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                py: "10px",
                px: "8px",
                borderRadius: "6px",
                background: COLORS.grayLightest,
                cursor: "pointer",
              }}
            >
              <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black, lineHeight: 1 }}>{pageSize}</MDTypography>
              <Box component="img" src="/images/icon/Down.png" alt="" sx={{ width: 24, height: 24, objectFit: "contain" }} />
            </Box>
            <Menu
              anchorEl={anchorElPageSize}
              open={Boolean(anchorElPageSize)}
              onClose={() => setAnchorElPageSize(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 80,
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    py: 1,
                    px: 2,
                    justifyContent: "center",
                    "&.Mui-selected": { background: COLORS.lighter, color: COLORS.main, fontWeight: 600 },
                    "&:hover": { background: COLORS.grayLightest },
                  },
                },
              }}
            >
              {[5, 10, 20, 50].map((n) => (
                <MenuItem key={n} selected={pageSize === n} onClick={() => { setPageSize(n); setAnchorElPageSize(null); }}>
                  {n}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>

        {/* table - CSS Grid */}
        <Box>
          {/* header row（標題 title/700/#8A8A8A）*/}
          <Box sx={{ display: "grid", gridTemplateColumns: "20% 22% 12% 18% 28%", px: 3, py: 1.5, background: COLORS.grayLightest, borderTop: `1px solid ${COLORS.grayLighter}`, borderBottom: `1px solid ${COLORS.grayLighter}` }}>
            {[
              LANGUAGE.Main.ColName || "用戶名稱",
              LANGUAGE.Main.ColEmail || "用戶 Email",
              LANGUAGE.Main.ColLevel || "層級",
              LANGUAGE.Main.ColCreatedAt || "註冊時間",
              LANGUAGE.Main.ColParent || "上級推薦人",
            ].map((col, i) => (
              <Box key={i} sx={{ ...TYPO.title, fontWeight: 700, color: COLORS.grayDark }}>{col}</Box>
            ))}
          </Box>

          {/* body rows（內文 bodyM/400/#000）*/}
          {pagedRows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center", color: COLORS.grayDark, ...TYPO.bodyM }}>
              {LANGUAGE.Main.Empty || "目前尚無資料"}
            </Box>
          ) : (
            pagedRows.map((row) => {
              const levelLabel =
                row.level === 1 ? LANGUAGE.Main.Level1 || "第一層"
                : row.level === 2 ? LANGUAGE.Main.Level2 || "第二層"
                : LANGUAGE.Main.Level3 || "第三層";
              const parentDisplay =
                row.parentName === "-" && row.parentEmail === "-"
                  ? "-"
                  : `${maskName(row.parentName)} (${maskEmail(row.parentEmail)})`;
              return (
                <Box
                  key={row.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "20% 22% 12% 18% 28%",
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${COLORS.grayLightest}`,
                    transition: "background 0.15s",
                    "&:hover": { background: COLORS.grayLightest },
                  }}
                >
                  <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{maskName(row.name)}</Box>
                  <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{maskEmail(row.email)}</Box>
                  <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{levelLabel}</Box>
                  <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{row.createdAt}</Box>
                  <Box sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{parentDisplay}</Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* footer：顯示X筆（bodyS/橘）+ 分頁（48×52 灰底圖示）*/}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 2, borderTop: `1px solid ${COLORS.grayLighter}` }}>
          <MDTypography sx={{ ...TYPO.bodyS, fontWeight: 400, color: COLORS.main }}>
            {footerText}
          </MDTypography>
          <Box display="flex" alignItems="center" gap={2}>
            {/* 往前一頁（arrow_right.png，依 Sean 指定）*/}
            <Box
              component="button"
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              sx={{ width: 48, height: 52, p: "14px 12px", border: "none", background: COLORS.grayLightest, display: "flex", alignItems: "center", justifyContent: "center", cursor: page <= 1 ? "default" : "pointer", opacity: page <= 1 ? 0.4 : 1 }}
            >
              <Box component="img" src="/images/icon/arrow_right.png" alt="prev" sx={{ width: 24, height: 24, objectFit: "contain" }} />
            </Box>
            <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 400, color: COLORS.black }}>{page}</MDTypography>
            {/* 往下一頁（arrow_left.png，依 Sean 指定）*/}
            <Box
              component="button"
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              sx={{ width: 48, height: 52, p: "14px 12px", border: "none", background: COLORS.grayLightest, display: "flex", alignItems: "center", justifyContent: "center", cursor: page >= totalPages ? "default" : "pointer", opacity: page >= totalPages ? 0.4 : 1 }}
            >
              <Box component="img" src="/images/icon/arrow_left.png" alt="next" sx={{ width: 24, height: 24, objectFit: "contain" }} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
